import React from 'react';
import { ExtraExpense } from '../../types';

interface Props {
  expenses: ExtraExpense[];
  remarks: string;
  operationRemarks: string;
  onChange: (updates: any) => void;
}

const WATER_KEY = "Water (1 Per Pax Per Day)";

const ExtraExpensesForm: React.FC<Props> = ({ expenses, remarks, operationRemarks, onChange }) => {
  const addExpense = () => {
    onChange({ extra_expenses: [...expenses, { expense: '', cost: 0.0 }] });
  };

  const removeExpense = () => {
    // Don't remove if last non-water expense would be removed, skip water row
    const nonWater = expenses.filter(e => e.expense !== WATER_KEY);
    if (nonWater.length > 0) {
      const lastNonWaterIdx = [...expenses].map((e, i) => ({ e, i })).filter(({ e }) => e.expense !== WATER_KEY).pop()!.i;
      onChange({ extra_expenses: expenses.filter((_, i) => i !== lastNonWaterIdx) });
    }
  };

  const updateExpense = (index: number, field: keyof ExtraExpense, value: any) => {
    const updated = [...expenses];
    updated[index] = { ...updated[index], [field]: value };
    onChange({ extra_expenses: updated });
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex gap-4 mb-6">
          <button onClick={addExpense} className="btn-primary">
            ➕ Add Expense
          </button>
          <button onClick={removeExpense} className="btn-secondary" disabled={expenses.filter(e => e.expense !== WATER_KEY).length === 0}>
            ➖ Remove Expense
          </button>
        </div>

        {expenses.length === 0 ? (
          <div className="text-center py-12 text-gray-500 border border-gray-200 rounded-lg">
            No extra expenses added yet. Click "Add Expense" to get started.
          </div>
        ) : (
          <div className="space-y-4">
            {expenses.map((expense, index) => {
              const isWater = expense.expense === WATER_KEY;
              return (
              <div key={index} className={`grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border rounded-lg ${
                isWater ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'
              }`}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expense {index + 1}
                    {isWater && <span className="ml-2 text-xs text-blue-600 font-normal">(auto-calculated)</span>}
                  </label>
                  <input
                    type="text"
                    value={expense.expense}
                    onChange={(e) => !isWater && updateExpense(index, 'expense', e.target.value)}
                    className={`input-field ${isWater ? 'bg-blue-100 text-blue-800 cursor-not-allowed' : ''}`}
                    placeholder="Enter expense name"
                    readOnly={isWater}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cost
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={expense.cost}
                    onChange={(e) => !isWater && updateExpense(index, 'cost', parseFloat(e.target.value) || 0.0)}
                    className={`input-field ${isWater ? 'bg-blue-100 text-blue-800 cursor-not-allowed' : ''}`}
                    placeholder="0.00"
                    readOnly={isWater}
                  />
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="border-t pt-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Remarks
          </label>
          <textarea
            value={remarks}
            onChange={(e) => onChange({ remarks: e.target.value })}
            className="input-field"
            rows={4}
            placeholder="Enter any remarks or special instructions..."
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Additional Info for Operations Team
        </label>
        <textarea
          value={operationRemarks}
          onChange={(e) => onChange({ operation_team_remarks: e.target.value })}
          className="input-field"
          rows={4}
          placeholder="Enter operational notes, special requirements, etc..."
        />
      </div>
    </div>
  );
};

export default ExtraExpensesForm;
